"use client";

import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { 
  connectSSE, 
  disconnectSSE, 
  clearEvents, 
  clearError,
  SSEEvent 
} from "@/store/slices/sse.slice";

export interface UseSSEOptions {
  /**
   * Whether to automatically connect on mount
   * @default true
   */
  autoConnect?: boolean;
  /**
   * Whether to automatically disconnect on unmount
   * @default true
   */
  autoDisconnect?: boolean;
  /**
   * Whether to clear events when the component unmounts
   * @default false
   */
  clearEventsOnUnmount?: boolean;
}

/**
 * Hook for managing SSE connection and accessing events
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { connected, events, connect, disconnect } = useSSE();
 *   
 *   // Listen for specific events
 *   useEffect(() => {
 *     const productEvents = events.filter(e => e.type.startsWith('product.'));
 *     if (productEvents.length > 0) {
 *       // Handle product events
 *     }
 *   }, [events]);
 *   
 *   return (
 *     <div>
 *       <p>Connection: {connected ? 'Connected' : 'Disconnected'}</p>
 *       <button onClick={connect}>Connect</button>
 *       <button onClick={disconnect}>Disconnect</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useSSE = (options: UseSSEOptions = {}) => {
  const {
    autoConnect = true,
    autoDisconnect = true,
    clearEventsOnUnmount = false,
  } = options;

  const dispatch = useAppDispatch();
  const sseState = useAppSelector((state) => state.sse);

  const connect = () => {
    dispatch(connectSSE());
  };

  const disconnect = () => {
    dispatch(disconnectSSE());
  };

  const clearAllEvents = () => {
    dispatch(clearEvents());
  };

  const clearConnectionError = () => {
    dispatch(clearError());
  };

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect && !sseState.connected && !sseState.connecting) {
      connect();
    }

    // Auto-disconnect on unmount if enabled
    return () => {
      if (autoDisconnect) {
        // disconnect();
      }
      if (clearEventsOnUnmount) {
        clearAllEvents();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  return {
    // State
    connected: sseState.connected,
    connecting: sseState.connecting,
    error: sseState.error,
    events: sseState.events,
    lastEvent: sseState.lastEvent,
    connectionRetries: sseState.connectionRetries,
    maxRetries: sseState.maxRetries,

    // Actions
    connect,
    disconnect,
    clearAllEvents,
    clearConnectionError,

    // Computed values
    isRetrying: sseState.connectionRetries > 0,
    canRetry: sseState.connectionRetries < sseState.maxRetries,
    connectionStatus: sseState.connected 
      ? 'connected' 
      : sseState.connecting 
        ? 'connecting' 
        : sseState.error 
          ? 'error' 
          : 'disconnected',
  };
};

/**
 * Hook for listening to specific SSE event types
 * 
 * @example
 * ```tsx
 * function ProductComponent() {
 *   const productEvents = useSSEEventListener(['product.created', 'product.updated']);
 *   
 *   useEffect(() => {
 *     if (productEvents.length > 0) {
 *       console.log('Product events:', productEvents);
 *       // Refresh product list or update UI
 *     }
 *   }, [productEvents]);
 * }
 * ```
 */
export const useSSEEventListener = (eventTypes: string[]): SSEEvent[] => {
  const events = useAppSelector((state) => state.sse.events);
  
  return events.filter(event => eventTypes.includes(event.type));
};

/**
 * Hook for getting the latest event of a specific type
 * 
 * @example
 * ```tsx
 * function OrderStatus() {
 *   const latestOrderEvent = useLatestSSEEvent('order.status_changed');
 *   
 *   if (latestOrderEvent) {
 *     // Update UI based on latest order status change
 *   }
 * }
 * ```
 */
export const useLatestSSEEvent = (eventType: string): SSEEvent | null => {
  const events = useAppSelector((state) => state.sse.events);
  
  // Find the latest event of the specified type
  const filteredEvents = events.filter(event => event.type === eventType);
  return filteredEvents.length > 0 ? filteredEvents[filteredEvents.length - 1] : null;
};

/**
 * Hook for SSE connection status only (lightweight)
 */
export const useSSEStatus = () => {
  const { connected, connecting, error } = useAppSelector((state) => state.sse);
  
  return {
    connected,
    connecting,
    error,
    status: connected ? 'connected' : connecting ? 'connecting' : error ? 'error' : 'disconnected',
  };
};